<template>
  <TabbedBox>
    <template v-slot:tablist>
      <Tab :first="true" tabid="tab-1">
        <i class="far fa-shopping-cart"></i> Sales Services Summary Detailed Report
      </Tab>
    </template>
    <template v-slot:tabcontent>
      <TabPage :first="true" tabid="tab-1">
        <div class="row">
          <div class="col-12">

            <ActionDropMenu class="float-right">
              <DropdownMenuItem @click="exportDataCSV" icon="fa-file-csv">Export CSV</DropdownMenuItem>
            </ActionDropMenu>

            <ReportFilterPopover
              class="float-right mr-2"
              headerTitle="Report Filter"
              :showApplyButton="true"
              :showResetButton="true"
              @apply="applySummaryFilter"
              @reset="resetSummaryFilter">
              <template v-slot:popover>
                <ReportFilterGroup label="Date Range">
                  <ReportDateRangeSelect 
                      :startDate="editingSummaryFilter.startDate"
                      :endDate="editingSummaryFilter.endDate"
                      @updateStartDate="editingSummaryFilter.startDate = $event"
                      @updateEndDate="editingSummaryFilter.endDate = $event"
                    />
                </ReportFilterGroup>

                <ReportFilterGroup label="Contact">
                  <ReportContactSelect
                    v-model="editingSummaryFilter.contactId"
                    @updateName="editingSummaryFilter.contactName = $event"
                  />
                </ReportFilterGroup>

                <ReportFilterGroup label="Stock Filter">
                  <FormMultiSelect v-model="editingSummaryFilter.stockList" :options="stockLookupList" />
                </ReportFilterGroup>

                <ReportFilterGroup label="Price Group Filter">
                  <FormMultiSelect v-model="editingSummaryFilter.priceGroupList" :options="priceGroupLookup" />
                </ReportFilterGroup>

                <ReportFilterGroup label="Service Range Filter">
                  <FormMultiSelect v-model="editingSummaryFilter.serviceRangeList" :options="serviceRangeLookup" />
                </ReportFilterGroup>
                
              </template>
              <template v-slot:filterDisplay>
                <div>
                  <i class="far fa-calendar mr-2"></i>{{appliedSummaryFilter.startDate | formatDate}} - {{appliedSummaryFilter.endDate | formatDate}}
                </div>
                <div v-if="appliedSummaryFilter.priceGroupList">
                  <i class="far fa-money-check-edit-alt mr-2"></i>{{appliedSummaryFilter.priceGroupList.length}} Price Group{{displayCountPlural(appliedSummaryFilter.priceGroupList.length)}}
                </div>
                <div v-if="appliedSummaryFilter.serviceRangeList">
                  <i class="far fa-tools mr-2"></i>{{appliedSummaryFilter.serviceRangeList.length}} Service Range{{displayCountPlural(appliedSummaryFilter.serviceRangeList.length)}}
                </div>
                <div v-if="appliedSummaryFilter.stockList && appliedSummaryFilter.stockList.length > 0">
                  <i class="far fa-inventory mr-2"></i>{{appliedSummaryFilter.stockList.length}} Stock{{displayCountPlural(appliedSummaryFilter.stockList.length)}}
                </div>
                <div v-if="appliedSummaryFilter.contactId">
                  <i class="far fa-user-circle mr-2"></i>{{appliedSummaryFilter.contactName}}
                </div>
              </template>
            </ReportFilterPopover>

            <ContentTitle>Sales Summary - Services Detailed Report</ContentTitle>
            <ContentSubTitle>Displays detailed sales data for service items sold during a date range</ContentSubTitle>
            <ContentSubTitle>Based on completed orders and completed order dates. All prices ex-GST</ContentSubTitle>
          </div>
        </div>
        <div class="row mt-2">
          <div class="col-12">

            <LoadingPanel :loading="loading" :loadingError="loadingError" />
            <div v-if="!loading">
              
              <StaticDataTable class="static-table-list" :striped="false">
                <template v-slot:header>
                  <tr>
                    <th> </th>
                    <th> </th>
                    <th :colspan="totalGroupCount" class="text-center table-data-alternate-column">Price Group Breakdown</th>
                  </tr>
                  <tr>
                    <th> </th>
                    <th> </th>
                    <th class="table-data-alternate-column" v-for="priceGroup of displayPriceGroupList" :key="priceGroup.id" :colspan="serviceRangesForPriceGroup(priceGroup).length">{{priceGroup.name}}</th>
                  </tr>
                  <tr>
                    <th>Stock</th>
                    <th>Total</th>
                    <template v-for="priceGroup of displayPriceGroupList">
                      <template v-for="serviceRange of serviceRangesForPriceGroup(priceGroup)">
                        <th class="table-data-alternate-column" :key="`pg-${priceGroup.id}-sr-${serviceRange.id}`">{{serviceRange.name}}</th>
                      </template>
                    </template>
                  </tr>
                </template>
                <template v-slot:body>
                  <template v-for="stock of reportData.stocks">
                    <tr class="static-datatable-striped" :key="`main-${stock.id}`">
                      <td class="font-weight-bold"><LinkStock :id="stock.id" :name="stock.name" /></td>
                      <td class="font-weight-bold">{{formatCurrency(stock.totalSales)}}</td>
                      <template v-for="priceGroupData of stock.priceGroupList">
                        <template v-for="serviceRangeData of priceGroupData.servicePriceRangeList">
                          <td class="font-weight-bold" :key="`${priceGroupData.id}-${serviceRangeData.id}`">{{formatCurrency(serviceRangeData.totalSales)}}</td>
                        </template>
                      </template>
                    </tr>
                    <tr class="table-row-sm" :key="`sqm-${stock.id}`">
                      <td><i class="fas fa-caret-right mr-1 ml-3"></i>{{formatLookupName(stock.uomType, UOMTypeLookup)}}</td>
                      <td><UOMValue :value="stock.totalUnits" :uomType="stock.uomType" /></td>
                      <template v-for="priceGroupData of stock.priceGroupList">
                        <template v-for="serviceRangeData of priceGroupData.servicePriceRangeList">
                          <td :key="`${priceGroupData.id}-${serviceRangeData.id}`">{{formatDecimal(serviceRangeData.totalUnits, 5)}} <span v-html="formatLookupName(stock.uomType, UOMTypeUnitsLookup)" /></td>
                        </template>
                      </template>
                    </tr>
                    <tr class="table-row-sm" :key="`avg-${stock.id}`">
                      <td><i class="fas fa-caret-right mr-1 ml-3"></i>Avg Sale Price / <span v-html="formatLookupName(stock.uomType, UOMTypeUnitsLookup)" /></td>
                      <td>{{formatCurrency(stock.avgSalePrice)}}</td>
                      <template v-for="priceGroupData of stock.priceGroupList">
                        <template v-for="serviceRangeData of priceGroupData.servicePriceRangeList">
                          <td :key="`${priceGroupData.id}-${serviceRangeData.id}`">{{formatCurrency(serviceRangeData.avgSalePrice)}}</td>
                        </template>
                      </template>
                    </tr>
                    <tr class="table-row-sm" :key="`spacer-${stock.id}`">
                      <td :colspan="totalGroupCount + 2"> </td>
                    </tr>
                  </template>
                  <tr class="border-top table-row-sm" v-if="reportData.stocks.length > 0">
                    <td class="font-weight-bold">Total Invoiced</td>
                    <td class="font-weight-bold">{{formatCurrency(reportData.totals.totalSales)}}</td>
                    <template v-for="priceGroupData of reportData.totals.priceGroupList">
                      <template v-for="serviceRangeData of priceGroupData.servicePriceRangeList">
                        <td :key="`${priceGroupData.id}-${serviceRangeData.id}`" class="font-weight-bold">
                          {{formatCurrency(serviceRangeData.totalSales)}}
                        </td>
                      </template>
                    </template>
                  </tr>
                </template>
                <template v-slot:empty v-if="reportData.stocks.length == 0">No sales data found</template>
              </StaticDataTable>

            </div>
          </div>
        </div>

      </TabPage>
    </template>
  </TabbedBox>
</template>


<script>
import _ from 'lodash'
import moment from 'moment'
import buildUrl from 'build-url'

import FormInputStockCode from '@/modules/inventory/components/formInputStockCode'
import PriceRequestSearchableSelect from '@/modules/pricing/components/priceRequestSearchableSelect'

import ReportFilterPopover from '@/components/reporting/reportFilterPopover'
import ReportDateRangeSelect from '@/components/reporting/reportDateRangeSelect'
import ReportFilterGroup from '@/components/reporting/reportFilterGroup'
import ReportContactSelect from '@/components/reporting/reportContactSelect'

import { InventoryStockTypes, UOMTypeLookup, UOMTypeUnitsLookup } from '@/store/types'

export default {
  name: 'SalesServicesDetailedSummary',
  components: {
    FormInputStockCode,
    PriceRequestSearchableSelect,
    ReportFilterPopover,
    ReportDateRangeSelect,
    ReportFilterGroup,
    ReportContactSelect
  },
  data() {
    return {
      loading: false,
      loadingError: '',

      defaultSummaryFilter: {    
        startDate: moment().local().startOf('month').toDate().toISOString(),
        endDate: moment().local().endOf('month').toDate().toISOString(),
        contactId: null,
        contactName: '',
        priceGroupList: [],
        serviceRangeList: [],
        stockList: []
      },
      editingSummaryFilter: {},
      appliedSummaryFilter: {},

      priceGroupList: [],
      priceGroupLookup: [],
      serviceRangeList: [],
      serviceRangeLookup: [],
      stockList: [],
      stockLookupList: [],
      reportData: {
        stockPriceGroups: [],
        stocks: [],
        totals: {}
      }
    }
  },
  computed: {
    displayPriceGroupList() {
      return this.priceGroupList.filter(priceGroup => {
        if(this.appliedSummaryFilter.priceGroupList.indexOf(priceGroup.id) == -1)
          return false

        // Hide if no service ranges
        return this.serviceRangesForPriceGroup(priceGroup).length > 0
      })
    },
    totalGroupCount() {
      return this.displayPriceGroupList.reduce((curValue, priceGroup) => {
        let serviceRangeList = this.serviceRangesForPriceGroup(priceGroup)
        return curValue + serviceRangeList.length
      }, 0)
    }
  },
  created() {
    this.InventoryStockTypes = InventoryStockTypes
    this.UOMTypeLookup = UOMTypeLookup
    this.UOMTypeUnitsLookup = UOMTypeUnitsLookup
  },
  mounted() {
    this.editingSummaryFilter = _.clone(this.defaultSummaryFilter)
    this.appliedSummaryFilter = _.clone(this.defaultSummaryFilter)

    this.loading = true
    this.requestData()
  },
  beforeDestroy() {            
  },
  methods: {
    requestData() {
      // Get config data
      Promise.all([
        this.$apiService.inventoryService.getStockList({}, { isActive: 1, type: InventoryStockTypes.glassService }),
        this.$apiService.pricingService.getStockPriceGroupList(),
        this.$apiService.pricingService.getServicePriceRangeList()
      ])
      .then(results => {
        this.stockList = results[0].data
        this.stockLookupList = this.stockList.map(stock => {
          return {
            value: stock.id,
            name: `${stock.code} | ${stock.name}`
          }
        })

        this.priceGroupList = results[1].data.filter(priceGroup => priceGroup.isActive)
        this.priceGroupLookup = this.priceGroupList.map(priceGroup => {
          return {
            value: priceGroup.id,
            name: priceGroup.name
          }
        })
        this.serviceRangeList = results[2].data.filter(serviceRange => serviceRange.isActive)
        this.serviceRangeLookup = this.serviceRangeList.map(serviceRange => {
          return {
            value: serviceRange.id,
            name: serviceRange.name
          }
        })

        // Default to all price groups selected
        this.defaultSummaryFilter.priceGroupList = this.priceGroupList.map(priceGroup => priceGroup.id)
        this.defaultSummaryFilter.serviceRangeList = this.serviceRangeList.map(serviceRange => serviceRange.id)
        this.resetSummaryFilter()
      })
    },
    requestReportData() {
      var params = {
        startDate: this.appliedSummaryFilter.startDate,
        endDate: this.appliedSummaryFilter.endDate,
        priceGroupIdList: this.appliedSummaryFilter.priceGroupList,
        servicePriceRangeIdList: this.appliedSummaryFilter.serviceRangeList,
        contactId: this.appliedSummaryFilter.contactId
      }
      if(this.appliedSummaryFilter.stockList && this.appliedSummaryFilter.stockList.length > 0)
        params.stockIdList = this.appliedSummaryFilter.stockList

      this.loading = true
      this.loadingError = ''
      this.$apiService.analyticsService.getOrdersValueServicesDetailedList(params)
      .then(response => {
        this.reportData = response.data
      })    
      .catch(err => {
        console.error(err)
        this.loadingError = 'Error loading data'
      })
      .finally(() => {
        this.loading = false
      })
    },
    exportDataCSV() {
      var params = {
        startDate: this.appliedSummaryFilter.startDate,
        endDate: this.appliedSummaryFilter.endDate,
        priceGroupIdList: this.appliedSummaryFilter.priceGroupList,
        contactId: this.appliedSummaryFilter.contactId,
        format: 'csv'
      }
      if(this.appliedSummaryFilter.stockList && this.appliedSummaryFilter.stockList.length > 0)
        params.stockIdList = this.appliedSummaryFilter.stockList

      let queryString = buildUrl({ queryParams: params })

      this.$apiService.reportingService.generateReportSignedURL(`/intelligence/orders/ordervalue/detailedlist/services${queryString}`)
      .then(response => {
        window.location.href = response.data.url
      })
    },
    serviceRangesForPriceGroup(priceGroup) {
      return priceGroup.servicePriceRanges.filter(serviceRange => {
        if(!serviceRange.isActive)
          return false

        return (this.appliedSummaryFilter.serviceRangeList.indexOf(serviceRange.id) != -1)
      })
    },
    applySummaryFilter() {
      this.appliedSummaryFilter = _.clone(this.editingSummaryFilter)

      this.requestReportData()
    },
    resetSummaryFilter() {
      this.editingSummaryFilter = _.clone(this.defaultSummaryFilter)
      this.appliedSummaryFilter = _.clone(this.defaultSummaryFilter)

      this.requestReportData()
    }
  }
}
</script>
